/**
 * Per-signature rate limit for auto-collected Datadog RUM errors.
 *
 * A client-side loop — an unhandled rejection re-thrown every frame, a retry
 * with no backoff — emits the *same* error thousands of times a minute. RUM's
 * own limiter is a coarse per-event-type ceiling (~3000 errors/min), not
 * per-signature, so a looping tab can sit under it while outspending the whole
 * fleet's error budget and burying real signal on every dashboard scoped to
 * the affected view. In production a single backgrounded tab produced ~256k
 * events in a day, peaking at ~33/sec.
 *
 * Each distinct signature gets a small budget per fixed window. The leading
 * occurrences always survive, so a loop stays diagnosable; only the tail is
 * dropped. Suppression reporting is best-effort: the count rides out on the
 * first kept event of the signature's next window, and is lost if the
 * signature is LRU-evicted first or never recurs.
 */

/** Events kept per signature per window. */
const BURST_LIMIT = 10;

const WINDOW_MS = 60_000;

/** Signatures tracked before the least-recently-seen is evicted. */
const MAX_TRACKED_SIGNATURES = 100;

const MAX_SIGNATURE_LENGTH = 200;

type Bucket = { windowStart: number; seen: number };

const buckets = new Map<string, Bucket>();

/**
 * Groups the events a loop repeats verbatim. A message carrying a varying
 * substring (a URL, a timestamp) signs differently each iteration, so it falls
 * through to the LRU bound below rather than to a shared budget — such a loop
 * is only memory-bounded, not throttled.
 */
export const buildRumErrorSignature = (error: {
  type?: string;
  message?: string;
}): string =>
  `${error.type ?? ''}|${(error.message ?? '').slice(0, MAX_SIGNATURE_LENGTH)}`;

export type RumErrorBurstVerdict = {
  /** False when this signature is over budget for the current window. */
  keep: boolean;
  /**
   * Events suppressed in this signature's last closed window. Best-effort:
   * lost on LRU eviction, and the window may have closed long ago.
   */
  droppedInPreviousWindow: number;
};

/**
 * Records one error against its signature's budget and reports whether it may
 * be sent. `now` is injected so callers (and tests) control the clock.
 */
export const admitRumError = (signature: string, now: number): RumErrorBurstVerdict => {
  const bucket = buckets.get(signature);
  const elapsed = bucket ? now - bucket.windowStart : 0;

  // A negative elapsed means the wall clock stepped backwards (NTP correction,
  // manual change); treat the window as expired so an over-budget signature
  // cannot freeze in the dropping state until the clock catches up.
  if (bucket && elapsed >= 0 && elapsed < WINDOW_MS) {
    bucket.seen += 1;
    // Re-insert so Map iteration order stays least-recently-seen first.
    buckets.delete(signature);
    buckets.set(signature, bucket);
    return { keep: bucket.seen <= BURST_LIMIT, droppedInPreviousWindow: 0 };
  }

  buckets.delete(signature);
  while (buckets.size >= MAX_TRACKED_SIGNATURES) {
    const oldest = buckets.keys().next().value;
    if (oldest === undefined) break;
    buckets.delete(oldest);
  }
  buckets.set(signature, { windowStart: now, seen: 1 });

  return {
    keep: true,
    droppedInPreviousWindow: bucket ? Math.max(0, bucket.seen - BURST_LIMIT) : 0,
  };
};

/** Test-only: clears every budget between cases. */
export const __resetRumErrorBurstThrottleForTests = (): void => {
  buckets.clear();
};
